import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./i18n/LanguageContext";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { api } from "./lib/api";
import { Loader2 } from "lucide-react";
import { ChatWidget } from "./components/ChatWidget";
import { WhatsAppButton } from "./components/WhatsAppButton";
import TreatmentDetailPage from "./pages/TreatmentDetailPage";
import TumorDetailPage from "./pages/TumorDetailPage";
import DoctorPage from "./pages/DoctorPage";
import ZarafetPage from "./pages/ZarafetPage";
import TechPage from "./pages/TechPage";
import AdsLanding from "./pages/AdsLanding";
import NewsPage from "./pages/NewsPage";
import NewsDetailPage from "./pages/NewsDetailPage";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import PatientPortal from "./pages/PatientPortal";
import AdminDashboard from "./pages/AdminDashboard";
import DocumentsPage from "./pages/DocumentsPage";

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { oauthLogin } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    api.post("/auth/google-session", { session_id: sessionId })
      .then((r) => {
        oauthLogin(r.data);
        navigate("/panel", { replace: true, state: { user: r.data } });
      })
      .catch(() => navigate("/giris", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-mist" data-testid="auth-callback">
      <Loader2 className="animate-spin text-brand" size={32} />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth returns with #session_id in the URL fragment — handle it before any route guard runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/giris" element={<AuthPage />} />
      <Route path="/panel" element={<PatientPortal />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/belgeler" element={<DocumentsPage />} />
      <Route path="/tedavi/:id" element={<TreatmentDetailPage />} />
      <Route path="/kanser/:id" element={<TumorDetailPage />} />
      <Route path="/doktor-mehmet-akdemir" element={<DoctorPage />} />
      <Route path="/zarafet" element={<ZarafetPage />} />
      <Route path="/teknoloji" element={<TechPage />} />
      <Route path="/basvuru" element={<AdsLanding />} />
      <Route path="/haberler" element={<NewsPage />} />
      <Route path="/haberler/:id" element={<NewsDetailPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09 });
    return () => lenis.destroy();
  }, []);

  return (
    <HelmetProvider>
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <ChatWidget />
          <WhatsAppButton />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
